import React, { FC, useCallback } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  DialogTitle,
  Divider,
  Drawer,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  styled,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { AccountStatus } from '../../../types';
import {
  accountsSelectors,
  accountsSlice,
  FilterAccountsBy,
  getUniqueFilterLists,
  selectAccountsPageFilters,
  SortAccountsBy,
  sortAccountsByOptions,
} from '../../../redux/accounts';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';

const StyledDrawer = styled(Drawer)`
  min-width: 388px;
  max-width: 400px;
  border-radius: 0px;

  .MuiDrawer-paper {
    min-width: 388px;
    max-width: 400px;
    border-radius: 0px;
  }
`;

const getFilterAccountsByList = (
  filterBy: FilterAccountsBy,
  uniqueFilterLists: {
    pdms: {
      value: string;
      label: string;
    }[];
    dms: {
      value: string;
      label: string;
    }[];
    cps: {
      value: string;
      label: string;
    }[];
  },
) => {
  if (filterBy === FilterAccountsBy.PortfolioDeliveryManager)
    return uniqueFilterLists.pdms;
  if (filterBy === FilterAccountsBy.DeliveryManager)
    return uniqueFilterLists.dms;

  return uniqueFilterLists.cps;
};

type FilterDrawerProps = {
  isOpen: boolean;
  onClose: () => void;
  resetFilters: () => void;
  isFiltersActive: boolean;
};

const ALL_AT_ONCE = 'All at once';
export const FilterDrawer: FC<FilterDrawerProps> = ({
  isOpen,
  onClose,
  resetFilters,
  isFiltersActive,
}) => {
  const dispatch = useAppDispatch();
  const uniqueFilterLists = useAppSelector(getUniqueFilterLists);
  const accountsPageFilters = useAppSelector(selectAccountsPageFilters);
  const pdmsToFilter = accountsPageFilters.PDMsToFilter;
  const dmsToFilter = accountsPageFilters.DMsToFilter;
  const sortBy = useAppSelector(accountsSelectors.getSortBy);

  const onChangeFilterValue = useCallback(
    (event: SelectChangeEvent<string[]>) => {
      const value = event.target.value as string;
      const name = event.target.name;

      dispatch(
        accountsSlice.actions.setAccountsPageFilters({
          [name]:
            // On autofill we get a stringified value.
            typeof value === 'string' ? value.split(',') : value,
        }),
      );
    },
    [dispatch],
  );

  const handleSortByChoose = useCallback(
    (
      event: React.SyntheticEvent<Element, Event>,
      value: {
        label: SortAccountsBy;
        value: SortAccountsBy;
      } | null,
    ) => {
      dispatch(
        accountsSlice.actions.setSortBy({
          sortBy: value?.value ?? SortAccountsBy.Margin,
        }),
      );
    },
    [dispatch],
  );

  const onDeleteFilterValue = useCallback(
    (value: string, name: string) => {
      const filterValues = name === 'PDMsToFilter' ? pdmsToFilter : dmsToFilter;
      dispatch(
        accountsSlice.actions.setAccountsPageFilters({
          [name]: filterValues.filter((x) => x !== value),
        }),
      );
    },
    [dispatch, dmsToFilter, pdmsToFilter],
  );

  return (
    <StyledDrawer anchor="right" open={isOpen} keepMounted onClose={onClose}>
      <DialogTitle style={{ display: 'flex', gap: 12 }}>
        Filters{' '}
        {isFiltersActive && (
          <Button
            variant="text"
            qa-id="dropdown-menu-button"
            onClick={resetFilters}
          >
            <RemoveCircleOutlineIcon fill="#448aff" />
            &nbsp;Reset all
          </Button>
        )}
      </DialogTitle>
      <IconButton
        aria-label="close"
        sx={{
          position: 'absolute',
          right: 8,
          top: 8,
        }}
        onClick={onClose}
      >
        <CloseIcon />
      </IconButton>
      <Grid container padding={2}>
        <Grid item xs={12} marginTop={2}>
          <FormControl fullWidth>
            <InputLabel id="filterValuelabel">Select PDM</InputLabel>
            <Select
              labelId="filterValuelabel"
              multiple
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((value) => {
                    return (
                      <Chip
                        key={value}
                        onDelete={() => {
                          onDeleteFilterValue(value, 'PDMsToFilter');
                          getFilterAccountsByList(
                            FilterAccountsBy.PortfolioDeliveryManager,
                            uniqueFilterLists,
                          );
                        }}
                        onMouseDown={(event) => {
                          event.stopPropagation();
                        }}
                        label={
                          uniqueFilterLists.pdms.find((x) => x.value === value)
                            ?.label || 'Name not found'
                        }
                      />
                    );
                  })}
                </Box>
              )}
              value={pdmsToFilter}
              label={`Select PDM`}
              onChange={onChangeFilterValue}
              name="PDMsToFilter"
            >
              {uniqueFilterLists.pdms.map((item: any) => (
                <MenuItem key={item.value} value={item.value}>
                  {item.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} marginTop={2}>
          <FormControl fullWidth>
            <InputLabel id="filterValuelabel">Select DM</InputLabel>
            <Select
              labelId="filterValuelabel"
              multiple
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((value) => {
                    return (
                      <Chip
                        key={value}
                        onDelete={() => {
                          onDeleteFilterValue(value, 'DMsToFilter');
                          getFilterAccountsByList(
                            FilterAccountsBy.DeliveryManager,
                            uniqueFilterLists,
                          );
                        }}
                        onMouseDown={(event) => {
                          event.stopPropagation();
                        }}
                        label={
                          uniqueFilterLists.dms.find((x) => x.value === value)
                            ?.label || 'Name not found'
                        }
                      />
                    );
                  })}
                </Box>
              )}
              value={dmsToFilter}
              label={`Select DM`}
              onChange={onChangeFilterValue}
              name="DMsToFilter"
            >
              {uniqueFilterLists.dms.map((item: any) => (
                <MenuItem key={item.value} value={item.value}>
                  {item.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        {/* Status filtering */}
        <Grid item xs={12} marginTop={2}>
          <FormControl fullWidth>
            <InputLabel id="Status">Status</InputLabel>
            <Select
              labelId="Status"
              value={
                accountsPageFilters.accountStatus === null
                  ? ALL_AT_ONCE
                  : accountsPageFilters.accountStatus
              }
              label="Status"
              onChange={(e) => {
                dispatch(
                  accountsSlice.actions.setAccountsPageFilters({
                    accountStatus:
                      e.target.value === ALL_AT_ONCE
                        ? null
                        : (e.target.value as AccountStatus),
                  }),
                );
              }}
            >
              {Object.values(AccountStatus).map((status) => (
                <MenuItem key={status} value={status}>
                  {status}
                </MenuItem>
              ))}
              <Divider component={'li'} />
              <MenuItem value={ALL_AT_ONCE}>{ALL_AT_ONCE}</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        {/* Sorting */}
        <Grid item xs={12} marginTop={2}>
          <Autocomplete
            sx={{
              '.MuiInputBase-adornedStart': { padding: '9px' },
              '& .MuiChip-root': { borderRadius: 4 },
            }}
            options={sortAccountsByOptions}
            onChange={handleSortByChoose}
            value={{ label: sortBy, value: sortBy }}
            renderOption={(props, option) => (
              <Box component="li" {...props}>
                <Box component="div" width="100%">
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                  >
                    <Typography
                      component="p"
                      sx={{ fontSize: '14px', color: 'text.primary' }}
                    >
                      {option.value}
                    </Typography>
                  </Stack>
                </Box>
              </Box>
            )}
            renderInput={(params) => (
              <TextField
                {...params}
                name="sortBy"
                label="Sorting"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: <>{params.InputProps.endAdornment}</>,
                }}
                InputLabelProps={{
                  shrink: true,
                }}
                placeholder="Please select"
              />
            )}
          />
        </Grid>
      </Grid>
    </StyledDrawer>
  );
};
